// Eigen WebwinkelKeur-widget (bouwronde 17-08, besluit JP).
//
// Waarom eigen: het officiële sidebar-script van WebwinkelKeur laadde
// wél op het portaal maar definieerde de <valued-widget>-elementen daar
// nooit (de widgetconfiguratie hangt aan voeronline.nl). Elke bezoeker
// zag dus permanent de tekst-fallback — een stille weigering die
// niemand opmerkte omdat de fallback te netjes was. Op voeronline.nl
// zelf draait trouwens óók geen WebwinkelKeur-script: het shopthema
// tekent het keurmerk zelf. Dat doen we hier nu ook, met exact hetzelfde
// beeld: vijf gele sterren, een oranje cirkel met het cijfer en het
// officiële hallmark ernaast.
//
// Cijfer en aantal komen live uit /api/public/reviews-score (de
// bestaande WebwinkelKeur-koppeling, server-side gecachet met de laatst
// bekende stand als terugval). Nooit een vastgetikt getal — harde eis
// van JP: de cijfers moeten kloppen met de reviewpagina waar het blok
// naartoe linkt.
//
// De sterren staan altijd vol, net als op voeronline.nl; het cijfer
// draagt de nuance. In de cirkel met een punt (9.7, zoals de shop), in
// lopende tekst met een komma (9,7).

import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";

import { useReviewsScore } from "@/lib/hooks/useReviewsScore";

import { WebwinkelKeurLogo } from "./WebwinkelKeurLogo";

export const WWK_REVIEWS_URL =
  "https://www.webwinkelkeur.nl/webshop/VoerOnline_1201140/reviews";

// Kleuren uit het shopthema — geen Tailwind-alias, dus als vaste waarde.
const STER_GEEL = "#FFC400";
const CIRKEL_ORANJE = "#EF7F01";

/** usp: sterren + cirkel + logo op één regel (desktop-balk).
 *  footer: sterren + cirkel op een regel, logo eronder, aantal eronder.
 *  compact: cirkel + logo (mobiel, waar geen regel past). */
export type WwkVariant = "usp" | "footer" | "compact";

type Maten = {
  ster: number;
  cirkel: number;
  cijfer: string;
  logo: string;
};

const MATEN: Record<WwkVariant, Maten> = {
  usp: { ster: 14, cirkel: 28, cijfer: "text-[12.5px]", logo: "h-[22px]" },
  footer: { ster: 16, cirkel: 34, cijfer: "text-[15px]", logo: "h-[28px]" },
  compact: { ster: 14, cirkel: 28, cijfer: "text-[12.5px]", logo: "h-[22px]" },
};

/** 9.7 — met punt, zoals de shop het in de cirkel zet. */
export function cijferInCirkel(score: number): string {
  return score.toFixed(1);
}

/** 9,7 — met komma, voor lopende tekst. */
export function cijferInTekst(score: number): string {
  return score.toFixed(1).replace(".", ",");
}

/** 10.474 — Nederlandse duizendtalscheiding. */
export function aantalInTekst(aantal: number): string {
  return new Intl.NumberFormat("nl-NL").format(aantal);
}

function Sterren({ maat }: { maat: number }) {
  return (
    <span aria-hidden className="flex shrink-0 items-center gap-[1px]">
      {[1, 2, 3, 4, 5].map((ster) => (
        <Star
          key={ster}
          width={maat}
          height={maat}
          fill={STER_GEEL}
          color={STER_GEEL}
          strokeWidth={1}
        />
      ))}
    </span>
  );
}

// De cirkel houdt zijn plek ook zonder cijfer: zolang het endpoint nog
// niets teruggaf blijft de ruimte gereserveerd, zodat het logo ernaast
// niet verspringt zodra het getal binnenkomt.
function ScoreCirkel({
  score,
  maten,
}: {
  score: number | null;
  maten: Maten;
}) {
  return (
    <span
      aria-hidden
      className={`grid shrink-0 place-items-center rounded-full font-bold leading-none text-white ${maten.cijfer}`}
      style={{
        width: maten.cirkel,
        height: maten.cirkel,
        backgroundColor: score === null ? "transparent" : CIRKEL_ORANJE,
      }}
    >
      {score === null ? "" : cijferInCirkel(score)}
    </span>
  );
}

export function WebwinkelKeurWidget({
  variant = "footer",
  className = "",
}: {
  variant?: WwkVariant;
  className?: string;
}) {
  const { t } = useTranslation();
  const { score, aantal } = useReviewsScore();
  const maten = MATEN[variant];

  // Het label vertelt wat er staat — inclusief het aantal, want de
  // zichtbare aantal-regel staat op aria-hidden. Ontbreekt het cijfer
  // (endpoint nog onderweg of alles weg), dan blijft de link gewoon
  // werken met een label zonder getallen.
  let label = t("retour.shop.wwk_link_label_kaal");
  if (score !== null && aantal !== null) {
    label = t("retour.shop.wwk_link_label_vol", {
      score: cijferInTekst(score),
      aantal: aantalInTekst(aantal),
    });
  } else if (score !== null) {
    label = t("retour.shop.wwk_link_label", { score: cijferInTekst(score) });
  }

  const gedeeld =
    "rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  if (variant === "footer") {
    return (
      <a
        href={WWK_REVIEWS_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        data-wwk-variant="footer"
        className={`inline-flex flex-col items-start gap-2 ${gedeeld} ${className}`}
      >
        <span className="flex items-center gap-2.5">
          <Sterren maat={maten.ster} />
          <ScoreCirkel score={score} maten={maten} />
        </span>
        <WebwinkelKeurLogo className={`${maten.logo} w-auto`} />
        {score !== null && aantal !== null && (
          <span aria-hidden className="text-[12.5px] text-muted-foreground">
            {t("retour.shop.wwk_aantal_regel", {
              score: cijferInTekst(score),
              aantal: aantalInTekst(aantal),
            })}
          </span>
        )}
      </a>
    );
  }

  return (
    <a
      href={WWK_REVIEWS_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-wwk-variant={variant}
      className={`inline-flex items-center gap-2 ${gedeeld} ${className}`}
    >
      {variant === "usp" && <Sterren maat={maten.ster} />}
      <ScoreCirkel score={score} maten={maten} />
      <WebwinkelKeurLogo className={`${maten.logo} w-auto`} />
    </a>
  );
}
